import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';

import { ListItem } from '../repositories/models/listItem';
import { Category } from '../repositories/models/category';
import TextField from '@mui/material/TextField';

// option shown at the end of the list when the typed text does not match an existing category
interface NewCategoryOption {
  inputValue: string,
  name: string
}

type CategoryOption = Category | NewCategoryOption

const isNewCategory = (option: CategoryOption | string): option is NewCategoryOption => typeof option !== 'string' && 'inputValue' in option

const filter = createFilterOptions<CategoryOption>()

interface ItemDialogArguments {
  open: boolean,
  item: ListItem | undefined,
  categories: Category[],
  onAddCategory: (name: string) => Promise<Category | null>,
  onSave: (id: string, name: string, categories: Category[]) => void,
  onDelete: () => void,
  onCancel: () => void
}

export default function ItemDialog({ open, item, categories, onAddCategory, onSave, onDelete, onCancel }: ItemDialogArguments) {
  const [text, setText] = useState('...')
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([])
  const [isAddingCategory, setIsAddingCategory] = useState(false)

  useEffect(() => {
    setText(item?.name || '')
    setSelectedCategories(item?.categories || [])
  }, [item, open])

  const addCategory = async (name: string) => {
    const trimmed = name.trim()

    if (trimmed.length === 0) {
      return
    }

    const existing = categories.find(c => c.name.toLowerCase() === trimmed.toLowerCase())

    if (existing) {
      setSelectedCategories(selected => selected.some(c => c.id === existing.id) ? selected : [...selected, existing])
      return
    }

    setIsAddingCategory(true)

    try {
      const created = await onAddCategory(trimmed)

      // null means adding failed - the error is already reported by the caller
      if (created === null) {
        return
      }

      setSelectedCategories(selected => selected.some(c => c.id === created.id) ? selected : [...selected, created])
    } finally {
      setIsAddingCategory(false)
    }
  }

  const onCategoriesChanged = (values: (CategoryOption | string)[]) => {
    const added = values.find(v => typeof v === 'string' || isNewCategory(v))
    setSelectedCategories(values.filter(v => typeof v !== 'string' && !isNewCategory(v)) as Category[])

    if (added !== undefined) {
      addCategory(typeof added === 'string' ? added : (added as NewCategoryOption).inputValue)
    }
  }

  return (
    <div>
      <Dialog
        open={open}
        onClose={onCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth='sm'
        fullWidth={true}
      >
        <DialogTitle id="alert-dialog-title">
            {item?.name}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <TextField id="standard-basic" fullWidth label="Item" variant="standard" onChange={ e => setText(e.target.value) } value={text} />
          </DialogContentText>

          <Autocomplete<CategoryOption, true, false, true>
            multiple
            freeSolo
            selectOnFocus
            handleHomeEndKeys
            disabled={isAddingCategory}
            style={{ marginTop: '16px' }}
            options={categories}
            value={selectedCategories}
            onChange={(_, values) => onCategoriesChanged(values)}
            isOptionEqualToValue={(option, value) => !isNewCategory(option) && !isNewCategory(value) && option.id === value.id}
            getOptionLabel={option => typeof option === 'string' ? option : option.name}
            filterOptions={(options, params) => {
              const filtered = filter(options, params)
              const input = params.inputValue.trim()

              if (input !== '' && !options.some(o => o.name.toLowerCase() === input.toLowerCase())) {
                filtered.push({ inputValue: input, name: `Add "${input}"` })
              }

              return filtered
            }}
            renderTags={(values, getTagProps) => values.map((option, index) =>
              <Chip size="small" label={typeof option === 'string' ? option : option.name} {...getTagProps({ index })} />
            )}
            renderInput={params => <TextField {...params} variant="standard" label="Categories" placeholder={isAddingCategory ? 'Adding...' : 'Select or add'} />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onSave(item!.id, text, selectedCategories)} color='success' disabled={isAddingCategory}>Save</Button>
          <Button onClick={onDelete} color='error'>Delete</Button>
          <Button onClick={onCancel} autoFocus>Cancel</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
